import language from '@/config/language';
import { roundedClassname, roundedDropdownClassname } from '@/helpers/environmentHelper';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectLanguage, setLanguage } from '@/state/reducers/languageSlice';
import { setGeneralLanguage } from '@/state/reducers/settingSlice';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { Menu, Transition } from '@headlessui/react';
import cn from 'classnames';
import { Fragment, useMemo } from 'react';
import { MdExpandMore, MdLanguage } from 'react-icons/md';
import { MenuItemButton } from '../layout/DesktopNavbar';

interface LanguageButtonProps {
    hasBorders?: boolean;
    direction?: 'left' | 'right';
    className?: string;
}

const LanguageButton = ({ hasBorders = true, direction = 'right', className }: LanguageButtonProps) => {
    const dispatch = useAppDispatch();
    const lang = useAppSelector(selectLanguage);

    const { mobile, tablet, interim } = useScreenSizes();
    const isMobile = useMemo(() => mobile || tablet || interim, [mobile, tablet, interim]);
    const labels = useMemo(() => language['en'].common, []);

    return (
        <Menu as="div" dir={lang === 'ar' ? 'rtl' : 'ltr'} className={cn('relative lg:my-0', { 'my-1': hasBorders })}>
            <Menu.Button
                type="button"
                className={cn(
                    'relative text-left text-sm flex gap-1 items-center text-brand-text transition duration-150 ease-in-out h-full whitespace-nowrap bg-brand-background-dark',
                    roundedClassname,
                    {
                        'border border-brand-neutral-700 p-1 rounded-md hover:border-brand-neutral-600': hasBorders,
                        'pl-1.5 pr-2.5': !isMobile && hasBorders && lang !== 'ar',
                        'pl-2.5 pr-1.5': !isMobile && hasBorders && lang === 'ar',
                        'py-2': !hasBorders,
                        'px-2 py-2': isMobile
                    }
                )}>
                <MdLanguage className={cn('', className, { 'w-5 h-5': !className })} />
                {!isMobile && (
                    <div>
                        {lang === 'en'
                            ? `${language[lang].common[lang]} (UK)`
                            : `${language[lang].common[lang]} / ${language['en'].common[lang]}`}
                    </div>
                )}
                {!hasBorders && (
                    <div className="ml-auto">
                        <MdExpandMore className="w-5 h-5" />
                    </div>
                )}
            </Menu.Button>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95">
                <Menu.Items
                    className={cn(
                        'absolute z-10 mt-2 w-48 origin-top-right divide-y divide-white/10 bg-brand-background-dark border border-brand-neutral-700 overflow-hidden',
                        roundedDropdownClassname,
                        { 'right-0': direction === 'right' },
                        { 'left-0': direction === 'left' }
                    )}>
                    <MenuItemButton
                        label={`${language['en'].common['en']} (UK)`}
                        onClick={() => {
                            dispatch(setLanguage('en'));
                            dispatch(setGeneralLanguage('en'));
                        }}
                    />
                    <MenuItemButton
                        label={`${language['dk'].common['dk']} / ${labels.dk}`}
                        onClick={() => {
                            dispatch(setLanguage('dk'));
                            dispatch(setGeneralLanguage('dk'));
                        }}
                    />
                    <MenuItemButton
                        label={`${language['zh'].common['zh']} / ${labels.zh}`}
                        onClick={() => {
                            dispatch(setLanguage('zh'));
                            dispatch(setGeneralLanguage('zh'));
                        }}
                    />
                    <MenuItemButton
                        label={`${language['ar'].common['ar']} / ${labels.ar}`}
                        onClick={() => {
                            dispatch(setLanguage('ar'));
                            dispatch(setGeneralLanguage('ar'));
                        }}
                    />
                    <MenuItemButton
                        label={`${language['es'].common['es']} / ${labels.es}`}
                        onClick={() => {
                            dispatch(setLanguage('es'));
                            dispatch(setGeneralLanguage('es'));
                        }}
                    />
                </Menu.Items>
            </Transition>
        </Menu>
    );
};

export default LanguageButton;
